import { useTypedSelector } from '../store';

import type { Tenant } from '../api/tenants';

// Per-tenant switches for modules not every tenant gets yet. Set by a platform admin in
// the tenant's metadata: {"features": ["approvals", "analytics"]}. UI visibility only.
export const FEATURES = {
	approvals: 'Approvals',
	analytics: 'Analytics',
	role_switcher: 'Role switcher (demo only)',
	demo_pages: 'Flow, commitments and proposal pages (demo only)',
	basys_theme: 'BASYS colors and font (v9 design)',
	statement_states: 'Upload and statement states (v9 layout)',
	roles: 'Sales, Pricing team, Manager roles (ISO profit hidden from Sales)',
	concierge: 'Concierge chat',
	partner_programs: 'Partner and risk tier on templates, Schedule A per partner',
	admin_created_accounts:
		'Admin-created accounts only (no sign-up, Microsoft sign-in)',
	bounce_replies: 'Reply drafts for bounced statements',
	// Read by the API: replies go out without review. Turn on only after the tenant approves it.
	bounce_reply_auto_send: 'Send bounce replies automatically',
	salesforce: 'Salesforce push (admin connects the org)',
	merchant_records: 'Up to three monthly statements as one merchant record',
} as const;

export type Feature = keyof typeof FEATURES;

export const hasFeature = (tenant: Tenant | null, feature: Feature): boolean =>
	tenant?.metadata?.features?.includes(feature) ?? false;

export const useFeature = (feature: Feature): boolean =>
	hasFeature(
		useTypedSelector((state) => state.tenant.tenant),
		feature,
	);

// The tenant update replaces the whole metadata object, so keep every other key.
export const withFeature = (
	metadata: Tenant['metadata'],
	feature: Feature,
	enabled: boolean,
): NonNullable<Tenant['metadata']> => {
	const others = (metadata?.features ?? []).filter((f) => f !== feature);
	return { ...metadata, features: enabled ? [...others, feature] : others };
};
